import React from "react";
import { cn } from "../../utils/cn";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  key?: string;
  label?: string;
  className?: string;
  /** Applied to the actual <input> element (e.g. "text-center"), unlike
   *  className which only reaches the outer bordered box */
  inputClassName?: string;
  value?: any;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  optional?: boolean;
  error?: boolean;
  helperText?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
}

const Input: React.FC<InputProps> = ({
  key = "",
  label,
  error,
  helperText,
  startAdornment,
  endAdornment,
  className,
  inputClassName,
  required = false,
  optional = false,
  ...props
}) => {
  return (
    <div key={key} className="flex flex-col items-start space-y-1">
      {label && (
        <label className="text-sm font-medium text-gray-700">
          {label} {optional && <span>(optional)</span>}{" "}
          {required && <span className="text-red-600">*</span>}
        </label>
      )}

      <div
        className={cn(
          "w-full flex items-center border rounded-md bg-white px-2 text-sm h-9 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500",
          error ? "border-red-500" : "border-gray-300",
          props.disabled && "bg-gray-100 cursor-not-allowed",
          className
        )}
      >
        {/* Start Adornment */}
        {startAdornment && (
          <div className="mr-1 text-gray-500 flex items-center shrink-0">
            {typeof startAdornment === "number" ||
            typeof startAdornment === "string" ? (
              <span>{startAdornment}</span>
            ) : (
              startAdornment
            )}
          </div>
        )}

        {/* Input Field */}
        <input
          {...props}
          className={cn(
            "flex-1 bg-transparent text-gray-900 placeholder-gray-400 outline-none px-1",
            "min-w-0 h-full", // ensures no overflow
            props.disabled && "cursor-not-allowed text-gray-500",
            inputClassName
          )}
        />

        {/* End Adornment */}
        {endAdornment && (
          <div className="ml-1 text-gray-500 flex items-center shrink-0">
            {typeof endAdornment === "number" ||
            typeof endAdornment === "string" ? (
              <span>{endAdornment}</span>
            ) : (
              endAdornment
            )}
          </div>
        )}
      </div>

      {helperText && (
        <span className={`text-xs ${error ? "text-red-500" : "text-gray-500"}`}>
          {helperText}
        </span>
      )}
    </div>
  );
};

export default Input;
